import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { ResilientEventSource, ConnectionState } from '../utils/ResilientEventSource';
import { SportEventContextValue, SportEventContextState, SportEventFilter } from '../types/SportEventTypes';


import { SportEvent, GameTimeSnapshot } from '../types/SportEventTypes';

import {
  getPlayByPlaySSEUrl,
  getTimeMappingSSEUrl,
  parsePlayByPlayEvent,
  parseGameTimeSnapshot,
  shouldIncludeEvent,
  saveResumeToken,
} from '../utils/sportEventUtils';

import useCustomEventsQuery from '../hooks/useCustomEventsQuery';
import { RevisionState } from '@c360/types';

interface SportEventProviderProps {
  children: ReactNode;
  gameId: string;
  maxEvents?: number;
  maxTimeMappings?: number;
}

const SportEventContext = createContext<SportEventContextValue | null>(null);

export const SportEventProvider: React.FC<SportEventProviderProps> = ({
  children,
  gameId,
  maxEvents = 2500,
  maxTimeMappings = 15000,
}) => {

  const [state, setState] = useState<SportEventContextState>({
    events: [],
    timeMappings: [],
    isConnected: false,
    eventsResumeToken: undefined,
    lastEventTimestamp: undefined,
    connectionError: undefined,
    filter: undefined,
  });

  const [historyLoaded, setHistoryLoaded] = useState(false);
  const historyLoadedRef = useRef(false); // Ref copy — read inside callbacks without triggering re-renders

  // Fetch custom events for this game
  const {
    data: customEvents = [],
  } = useCustomEventsQuery(gameId);


  // Separate refs for each stream
  const eventsSSERef = useRef<ResilientEventSource | null>(null);
  const timeMappingSSERef = useRef<ResilientEventSource | null>(null);

  // Connection state for UI feedback
  const [eventsConnectionState, setEventsConnectionState] = useState<ConnectionState>('disconnected');
  const [timeMappingConnectionState, setTimeMappingConnectionState] = useState<ConnectionState>('disconnected');
  const filterRef = useRef<SportEventFilter | undefined>(undefined);
  const timeMappingKeysRef = useRef<Set<string>>(new Set());

  // ✅ NEW: Ref-based event storage with O(1) indexing
  const eventsMapRef = useRef<Map<string, SportEvent>>(new Map());
  const eventsArrayRef = useRef<SportEvent[]>([]);
  const pendingUpdatesRef = useRef<boolean>(false);

  // Store custom events in a ref to avoid stale closures in flush callback
  const customEventsRef = useRef<SportEvent[]>([]);
  customEventsRef.current = customEvents;

  // ✅ NEW: Ref-based time mapping storage with batching
  const timeMappingsArrayRef = useRef<GameTimeSnapshot[]>([]);
  const pendingTimeMappingUpdatesRef = useRef<boolean>(false);

  // Track connection states
  const [eventsConnected, setEventsConnected] = useState(false);
  const [timeMappingConnected, setTimeMappingConnected] = useState(false);

  // Track if streams have been gracefully closed (no reconnection needed)
  const eventsClosedRef = useRef(false);
  const timeMappingClosedRef = useRef(false);

  // Track if the server signalled an in-band error since the last connect.
  // The BFF sends both event:'error' AND event:'end' when the upstream gRPC
  // dies, and without this flag we'd treat the 'end' as graceful and never
  // reconnect.
  const eventsErroredRef = useRef(false);
  const timeMappingErroredRef = useRef(false);

  // ============================================================================
  // Throttled Batch Update (prevents render thrashing)
  // ============================================================================

  const flushEventsToState = useCallback(() => {
    if (!pendingUpdatesRef.current) return;

    pendingUpdatesRef.current = false;

    // Build sorted array from Map (only once, not per event!)
    const sesEventsArray = Array.from(eventsMapRef.current.values());
    const customEventsArray = customEventsRef.current; // Use ref to avoid stale closure

    // Apply filter to both SES and custom events before merging
    const filteredSesEvents = filterRef.current
      ? sesEventsArray.filter((event) => shouldIncludeEvent(event, filterRef.current))
      : sesEventsArray;
    const filteredCustomEvents = filterRef.current
      ? customEventsArray.filter((event) => shouldIncludeEvent(event, filterRef.current))
      : customEventsArray;

    // Merge SES events with custom events
    const mergedEvents = [...filteredSesEvents, ...filteredCustomEvents.map(parsePlayByPlayEvent)];
    eventsArrayRef.current = mergedEvents;

    // Single state update for ALL accumulated changes
    setState((prevState) => ({
      ...prevState,
      events: mergedEvents,
    }));
  }, []); // No dependencies - uses refs only

  // Throttle: Only update UI every 100ms max (batches rapid updates)
  const scheduleFlush = useCallback(() => {
    if (pendingUpdatesRef.current) return; // Already scheduled

    pendingUpdatesRef.current = true;

    // to prevent violations
    setTimeout(() => {
      flushEventsToState();
    }, 0);
  }, [flushEventsToState]);

  // ============================================================================
  // Time Mapping Batch Update
  // ============================================================================

  const flushTimeMappingsToState = useCallback(() => {
    if (!pendingTimeMappingUpdatesRef.current) return;

    pendingTimeMappingUpdatesRef.current = false;

    // Single state update for ALL accumulated time mapping changes
    setState((prevState) => {
      // Only update if the array reference actually changed
      if (prevState.timeMappings === timeMappingsArrayRef.current) {
        return prevState;
      }

      return {
        ...prevState,
        timeMappings: timeMappingsArrayRef.current,
      };
    });
  }, []);

  const scheduleTimeMappingFlush = useCallback(() => {
    if (pendingTimeMappingUpdatesRef.current) return; // Already scheduled

    pendingTimeMappingUpdatesRef.current = true;

    setTimeout(() => {
      flushTimeMappingsToState();
    }, 0);
  }, [flushTimeMappingsToState]);

  // ============================================================================
  // Event Management
  // ============================================================================

  const addOrUpdateEvent = useCallback(
    (newEvent: SportEvent) => {
      // ✅ NEW: O(1) Map operations instead of O(n) array operations
      const eventsMap = eventsMapRef.current;

      if (!newEvent.sourceNativeId) {
        console.warn('Event missing sourceNativeId, skipping:', newEvent);
        return;
      }

      // A DELETE only needs the id, so it runs before the display filter — a
      // bare DELETE carries no payload and would otherwise be filtered out,
      // leaving the deleted row on screen until a refresh.
      if (newEvent.revision?.state === RevisionState.DELETE) {
        eventsMap.delete(newEvent.sourceNativeId); // O(1) delete
        scheduleFlush();
        return;
      }

      if (!shouldIncludeEvent(newEvent, filterRef.current)) {
        return;
      }

      if (!newEvent.revision || !newEvent.revision.state) {
        eventsMap.set(newEvent.sourceNativeId, newEvent); // O(1) insert
        return;
      }
      // Handle revision states
      if (newEvent.revision.state === RevisionState.UPDATE) {
        // console.trace('Updating event with ID:', newEvent.id);
        eventsMap.set(newEvent.sourceNativeId, newEvent); // O(1) update/insert
      } else {
        // INSERT or UNKNOWN - add new event
        if (!eventsMap.has(newEvent.sourceNativeId)) {
          eventsMap.set(newEvent.sourceNativeId, newEvent); // O(1) insert
        }
      }

      // Keep only the most recent events if we exceed maxEvents
      if (eventsMap.size > maxEvents) {
        // Convert to array, sort by timestamp, remove oldest
        const eventsArray = Array.from(eventsMap.values());
        eventsArray.sort((a, b) => {
          const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return timeA - timeB;
        });

        const toRemove = eventsArray.slice(0, eventsArray.length - maxEvents);
        toRemove.forEach((e) => eventsMap.delete(e.sourceNativeId!));
      }

      // Save resume token if present (update state directly for this metadata)
      if (newEvent.resumeToken) {
        saveResumeToken(gameId, newEvent.resumeToken);
        setState((prevState) => {
          // ✅ Only update if token or timestamp actually changed
          if (
            prevState.eventsResumeToken === newEvent.resumeToken &&
            prevState.lastEventTimestamp === newEvent.timestamp
          ) {
            return prevState; // No change
          }

          return {
            ...prevState,
            eventsResumeToken: newEvent.resumeToken,
            lastEventTimestamp: newEvent.timestamp,
          };
        });
      }

      // Schedule a batched UI update (throttled to 60fps)
      scheduleFlush();
    },
    [maxEvents, gameId, scheduleFlush],
  );

  const addTimeMapping = useCallback(
    (newMapping: GameTimeSnapshot) => {
      if (!newMapping.key) {
        console.warn('Time mapping missing key, skipping');
        return;
      }

      // Check for duplicates using the key
      if (timeMappingKeysRef.current.has(newMapping.key)) {
        return;
      }

      // ✅ NEW: Update ref-based storage instead of immediate state update
      const updatedMappings = [...timeMappingsArrayRef.current, newMapping];

      // Keep only the most recent mappings if we exceed maxTimeMappings
      if (updatedMappings.length > maxTimeMappings) {
        const removedMappings = updatedMappings.splice(0, updatedMappings.length - maxTimeMappings);
        // Remove the keys from our duplicate tracking set
        removedMappings.forEach((mapping) => {
          if (mapping.key) {
            timeMappingKeysRef.current.delete(mapping.key);
          }
        });
      }

      // Add the new key to our tracking set
      timeMappingKeysRef.current.add(newMapping.key!);

      // Update ref with new array
      timeMappingsArrayRef.current = updatedMappings;

      // Schedule a batched UI update (throttled)
      scheduleTimeMappingFlush();
    },
    [maxTimeMappings, scheduleTimeMappingFlush],
  );

  // ============================================================================
  // Play-by-Play Event Stream Handlers
  // ============================================================================

  const handlePlayByPlayMessage = useCallback(
    (rawData: any) => {
      try {
        // The backend sends individual events, not chunks
        const sportEvent = parsePlayByPlayEvent(rawData);
        addOrUpdateEvent(sportEvent);
      } catch (error) {
        console.error('[HANDLER] Failed to parse play-by-play event:', error, rawData);
      }
    },
    [addOrUpdateEvent],
  );

  const handlePlayByPlayOpen = useCallback(() => {
    setEventsConnected(true);
  }, []);

  const handlePlayByPlayError = useCallback((error: Event) => {
    // Don't log errors if stream was gracefully closed
    if (eventsClosedRef.current) {
      return;
    }

    console.error('Play-by-play SSE error:', error);
    setEventsConnected(false);
    setState((prevState) => ({
      ...prevState,
      connectionError: 'Play-by-play connection error occurred',
    }));
  }, []);

  // ============================================================================
  // Time Mapping Stream Handlers
  // ============================================================================

  const handleTimeMappingMessage = useCallback(
    (rawData: any) => {
      try {
        // Handle both single snapshots and arrays of snapshots
        if (Array.isArray(rawData)) {
          // Backend sends arrays for live games via gRPC
          rawData.forEach((snapshot) => {
            const parsed = parseGameTimeSnapshot(snapshot);
            addTimeMapping(parsed);
          });

          // Mark history as loaded after first message
          if (!historyLoadedRef.current) {
            historyLoadedRef.current = true;
            setHistoryLoaded(true);
          }
        } else {
          // Backend sends individual snapshots for recorded games
          const parsed = parseGameTimeSnapshot(rawData);
          addTimeMapping(parsed);

          // Mark history as loaded after receiving snapshots from recorded games
          if (!historyLoadedRef.current) {
            historyLoadedRef.current = true;
            setHistoryLoaded(true);
          }
        }
      } catch (error) {
        console.error('Failed to parse time mapping:', error, rawData);
      }
    },
    [addTimeMapping], // historyLoaded removed — read via ref to avoid stale closure re-renders
  );

  const handleTimeMappingOpen = useCallback(() => {
    setTimeMappingConnected(true);
  }, []);

  const handleTimeMappingError = useCallback((error: Event) => {
    // Don't log errors if stream was gracefully closed
    if (timeMappingClosedRef.current) {
      return;
    }

    console.error('Time mapping SSE error:', error);
    setTimeMappingConnected(false);
    setState((prevState) => ({
      ...prevState,
      connectionError: 'Time mapping connection error occurred',
    }));
  }, []);

  // ============================================================================
  // Connection Management
  // ============================================================================

  const connectPlayByPlayStream = useCallback(() => {
    // Don't reconnect if stream was gracefully closed
    if (eventsClosedRef.current) {
      return;
    }

    if (eventsSSERef.current) {
      eventsSSERef.current.close();
    }

    const url = getPlayByPlaySSEUrl(gameId);

    const eventSource = new ResilientEventSource(url, {
      initialRetryDelay: 1000,
      maxRetryDelay: 30000,
      backoffMultiplier: 2,
      maxRetries: Infinity, // Keep trying for live games
      staleTimeout: 60000, // 1 minute without data = stale
      staleCheckInterval: 15000,
      onStateChange: (state) => {
        setEventsConnectionState(state);
        if (state === 'stale') {
          console.warn('[FRONTEND PLAY-BY-PLAY] Connection stale, forcing reconnect...');
        }
      },
      onStaleConnection: () => {
        console.warn('[FRONTEND PLAY-BY-PLAY] Stale connection detected - auto-reconnecting');
      },
    });
    eventsSSERef.current = eventSource;

    // Main message handler - routes events based on parsed data.
    // Defensive: some frames may carry multiple newline-delimited JSON envelopes.
    eventSource.addEventListener('message', (event: MessageEvent) => {
      for (const line of event.data.split('\n').filter((l: string) => l.trim())) {
        try {
          const parsed = JSON.parse(line);
          const eventType = parsed.event || 'unknown';
          const eventData = parsed.data || parsed;

          switch (eventType) {
            case 'play-by-play': {
              eventData.events.forEach((ev: any) => {
                handlePlayByPlayMessage(ev);
              });
              break;
            }

            case 'initialized':
              break;

            case 'ping':
              break;

            case 'error':
              // The BFF lost its upstream gRPC stream. Flag it so the matching
              // event:'end' that follows isn't treated as graceful close, then
              // drive a backoff retry through ResilientEventSource.
              console.warn(
                '[FRONTEND PLAY-BY-PLAY] Server-signalled stream error, retrying:',
                eventData,
              );
              eventsErroredRef.current = true;
              eventSource.triggerRetry();
              break;

            case 'end':
              if (eventsErroredRef.current) {
                // Error-induced end; the retry is already in flight from the
                // 'error' branch. Don't markStreamEnded — that would disable
                // reconnection. Reset the flag so a future genuine end is
                // honored.
                eventsErroredRef.current = false;
                break;
              }
              eventsClosedRef.current = true;
              setEventsConnected(true);
              eventSource.markStreamEnded();
              eventSource.close();
              eventsSSERef.current = null;
              break;

            default:
              console.warn(`[FRONTEND PLAY-BY-PLAY] Unknown event type: ${eventType}`, eventData);
          }
        } catch (error) {
          console.error('[FRONTEND PLAY-BY-PLAY] Failed to parse SSE message:', error, line);
        }
      }
    });

    eventSource.onOpen(() => {
      // Successful (re)connect — clear any error flag from a previous attempt.
      eventsErroredRef.current = false;
      handlePlayByPlayOpen();
    });
    eventSource.onError(handlePlayByPlayError);

    // Start the connection
    eventSource.connect();
  }, [gameId, handlePlayByPlayMessage, handlePlayByPlayOpen, handlePlayByPlayError]);

  const connectTimeMappingStream = useCallback(() => {
    // Don't reconnect if stream was gracefully closed
    if (timeMappingClosedRef.current) {
      return;
    }

    if (timeMappingSSERef.current) {
      timeMappingSSERef.current.close();
    }

    const url = getTimeMappingSSEUrl(gameId);

    const eventSource = new ResilientEventSource(url, {
      initialRetryDelay: 1000,
      maxRetryDelay: 30000,
      backoffMultiplier: 2,
      maxRetries: Infinity, // Keep trying for live games
      staleTimeout: 60000,
      staleCheckInterval: 15000,
      onStateChange: (state) => {
        setTimeMappingConnectionState(state);
      },
    });
    timeMappingSSERef.current = eventSource;

    // Main message handler - routes events based on parsed data.
    // Defensive: some frames may carry multiple newline-delimited JSON envelopes.
    eventSource.addEventListener('message', (event: MessageEvent) => {
      for (const line of event.data.split('\n').filter((l: string) => l.trim())) {
        try {
          const parsed = JSON.parse(line);
          const eventType = parsed.event || 'unknown';
          const eventData = parsed.data || parsed;

          switch (eventType) {
            case 'time-map':
              handleTimeMappingMessage(eventData);
              break;

            case 'initialized':
              break;

            case 'ping':
              break;

            case 'error':
              console.warn(
                '[FRONTEND TIME-MAPPING] Server-signalled stream error, retrying:',
                eventData,
              );
              timeMappingErroredRef.current = true;
              eventSource.triggerRetry();
              break;

            case 'end':
              if (timeMappingErroredRef.current) {
                timeMappingErroredRef.current = false;
                break;
              }
              timeMappingClosedRef.current = true;
              setTimeMappingConnected(true);
              historyLoadedRef.current = true;
              setHistoryLoaded(true);
              eventSource.markStreamEnded();
              eventSource.close();
              timeMappingSSERef.current = null;
              break;

            default:
              console.warn(`[FRONTEND TIME-MAPPING] Unknown event type: ${eventType}`, eventData);
          }
        } catch (error) {
          console.error('[FRONTEND TIME-MAPPING] Failed to parse SSE message:', error, line);
        }
      }
    });

    eventSource.onOpen(() => {
      timeMappingErroredRef.current = false;
      handleTimeMappingOpen();
    });
    eventSource.onError(handleTimeMappingError);

    // Start the connection
    eventSource.connect();
  }, [gameId, handleTimeMappingMessage, handleTimeMappingOpen, handleTimeMappingError]);

  const connect = useCallback(() => {
    connectPlayByPlayStream();
    connectTimeMappingStream();
  }, [connectPlayByPlayStream, connectTimeMappingStream]);

  const disconnect = useCallback(() => {
    if (eventsSSERef.current) {
      eventsSSERef.current.close();
      eventsSSERef.current = null;
    }
    if (timeMappingSSERef.current) {
      timeMappingSSERef.current.close();
      timeMappingSSERef.current = null;
    }
    setEventsConnected(false);
    setTimeMappingConnected(false);
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    setTimeout(() => connect(), 1000);
  }, [disconnect, connect]);

  /**
   * Force reconnect both streams - useful when UI stops updating
   * This resets the closed flags and forces a fresh connection
   */
  const forceReconnect = useCallback(() => {
    // Reset closed flags to allow reconnection
    eventsClosedRef.current = false;
    timeMappingClosedRef.current = false;

    // Use the ResilientEventSource reconnect method for clean reconnection
    if (eventsSSERef.current) {
      eventsSSERef.current.reconnect();
    } else {
      connectPlayByPlayStream();
    }

    if (timeMappingSSERef.current) {
      timeMappingSSERef.current.reconnect();
    } else {
      connectTimeMappingStream();
    }
  }, [connectPlayByPlayStream, connectTimeMappingStream]);

  // ============================================================================
  // Filter and Helper Methods
  // ============================================================================

  const setFilter = useCallback(
    (filter?: SportEventFilter) => {
      filterRef.current = filter;
      setState((prevState) => ({
        ...prevState,
        filter,
      }));
      // Re-merge with new filter
      scheduleFlush();
    },
    [scheduleFlush],
  );

  const clearEvents = useCallback(() => {
    eventsMapRef.current.clear();
    eventsArrayRef.current = [];
    setState((prevState) => ({
      ...prevState,
      events: [],
      lastEventTimestamp: undefined,
    }));
  }, []);

  const clearTimeMappings = useCallback(() => {
    timeMappingKeysRef.current.clear();
    timeMappingsArrayRef.current = []; // ✅ Clear ref as well
    setState((prevState) => ({
      ...prevState,
      timeMappings: [],
    }));
    historyLoadedRef.current = false;
    setHistoryLoaded(false);
  }, []);

  const getTimeMappingForGameTime = useCallback(
    (gameTime: string, period?: number) => {
      return state.timeMappings.find(
        (mapping) =>
          mapping.gameTime === gameTime && (period === undefined || mapping.period === period),
      );
    },
    [state.timeMappings],
  );

  const getLatestTimeMapping = useCallback(() => {
    return state.timeMappings.length > 0 ? state.timeMappings[state.timeMappings.length - 1] : null;
  }, [state.timeMappings]);

  // Memoize sorted time mappings to avoid re-sorting on every access
  const sortedTimeMappings = useMemo(() => {
    if (!state.timeMappings || state.timeMappings.length === 0) return [];
    return [...state.timeMappings].sort((a, b) => {
      const timeA = a.timestamp || 0;
      const timeB = b.timestamp || 0;
      return timeA - timeB;
    });
  }, [state.timeMappings]);

  const getSortedTimeMappings = useCallback(() => {
    return sortedTimeMappings;
  }, [sortedTimeMappings]);

  // Binary search to find the latest game-time mapping at or before a UTC timestamp.
  // A future mapping must not label an earlier playback position (for example,
  // Q1 must not replace an NBA break before Q1 has actually begun).
  const findTimeMappingForTimestamp = useCallback(
    (targetUtc: number): GameTimeSnapshot | null => {
      const mappings = sortedTimeMappings;
      if (mappings.length === 0) return null;

      let left = 0;
      let right = mappings.length - 1;
      let latest: GameTimeSnapshot | null = null;

      // Handle edge cases
      if (targetUtc < mappings[0].timestamp) {
        return null;
      }
      if (targetUtc === mappings[0].timestamp) {
        return mappings[0];
      }
      if (targetUtc >= mappings[right].timestamp) {
        return mappings[right];
      }

      // Binary search for the mapping immediately preceding the target.
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const current = mappings[mid];

        if (current.timestamp === targetUtc) {
          return current;
        }

        if (current.timestamp < targetUtc) {
          latest = current;
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }

      return latest;
    },
    [sortedTimeMappings],
  );

  // ============================================================================
  // Effects
  // ============================================================================

  // Reset state when gameId changes
  useEffect(() => {
    clearEvents();
    clearTimeMappings(); // also resets historyLoadedRef + historyLoaded state

    // Reset closed flags to allow connection to new game
    eventsClosedRef.current = false;
    timeMappingClosedRef.current = false;
  }, [gameId, clearEvents, clearTimeMappings]);

  // Connect to streams when gameId is available
  useEffect(() => {
    if (gameId) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [gameId, connect, disconnect]);

  // Update overall connection status
  useEffect(() => {
    const isConnected = eventsConnected && timeMappingConnected;
    setState((prevState) => {
      // ✅ CRITICAL: Only update if isConnected actually changed
      // This prevents infinite render loops from redundant state updates
      const connectionErrorChanged = isConnected && prevState.connectionError !== undefined;

      if (prevState.isConnected === isConnected && !connectionErrorChanged) {
        return prevState; // No change, return same state reference
      }

      return {
        ...prevState,
        isConnected,
        connectionError: isConnected ? undefined : prevState.connectionError,
      };
    });
  }, [eventsConnected, timeMappingConnected]);

  // Re-merge events when custom events change (create/update/delete mutations)
  useEffect(() => {
    scheduleFlush();
  }, [customEvents, scheduleFlush]);

  // ============================================================================
  // Context Value
  // ============================================================================

  // 🔍 DEBUG: Track which dependency causes contextValue to recreate
  const prevContextDepsRef = useRef({
    state,
    setFilter,
    clearEvents,
    clearTimeMappings,
    reconnect,
    disconnect,
    getTimeMappingForGameTime,
    getLatestTimeMapping,
    getSortedTimeMappings,
    findTimeMappingForTimestamp,
    historyLoaded,
  });

  useEffect(() => {
    const prev = prevContextDepsRef.current;
    const changes: string[] = [];

    // Deep track state changes
    if (prev.state !== state) {
      changes.push('🔴 state');

      // Detailed state property tracking
      if (prev.state.events !== state.events) {
        changes.push(
          `  ↳ state.events (${prev.state.events.length} → ${state.events.length} items)`,
        );
      }
      if (prev.state.timeMappings !== state.timeMappings) {
        changes.push(
          `  ↳ state.timeMappings (${prev.state.timeMappings.length} → ${state.timeMappings.length} items)`,
        );
      }
      if (prev.state.isConnected !== state.isConnected) {
        changes.push(`  ↳ state.isConnected (${prev.state.isConnected} → ${state.isConnected})`);
      }
      if (prev.state.eventsResumeToken !== state.eventsResumeToken) {
        changes.push(`  ↳ state.eventsResumeToken`);
      }
      if (prev.state.lastEventTimestamp !== state.lastEventTimestamp) {
        changes.push(`  ↳ state.lastEventTimestamp`);
      }
      if (prev.state.connectionError !== state.connectionError) {
        changes.push(`  ↳ state.connectionError`);
      }
      if (prev.state.filter !== state.filter) {
        changes.push(`  ↳ state.filter`);
      }
    }

    if (prev.setFilter !== setFilter) changes.push('🔴 setFilter');
    if (prev.clearEvents !== clearEvents) changes.push('🔴 clearEvents');
    if (prev.clearTimeMappings !== clearTimeMappings) changes.push('🔴 clearTimeMappings');
    if (prev.reconnect !== reconnect) changes.push('🔴 reconnect');
    if (prev.disconnect !== disconnect) changes.push('🔴 disconnect');
    if (prev.getTimeMappingForGameTime !== getTimeMappingForGameTime)
      changes.push('🔴 getTimeMappingForGameTime');
    if (prev.getLatestTimeMapping !== getLatestTimeMapping) changes.push('🔴 getLatestTimeMapping');
    if (prev.getSortedTimeMappings !== getSortedTimeMappings)
      changes.push('🔴 getSortedTimeMappings');
    if (prev.findTimeMappingForTimestamp !== findTimeMappingForTimestamp)
      changes.push('🔴 findTimeMappingForTimestamp');
    if (prev.historyLoaded !== historyLoaded) changes.push('🔴 historyLoaded');

    prevContextDepsRef.current = {
      state,
      setFilter,
      clearEvents,
      clearTimeMappings,
      reconnect,
      disconnect,
      getTimeMappingForGameTime,
      getLatestTimeMapping,
      getSortedTimeMappings,
      findTimeMappingForTimestamp,
      historyLoaded,
    };
  });

  // ✅ CRITICAL: Memoize context value to prevent infinite re-render cascade
  const contextValue: SportEventContextValue = useMemo(
    () => ({
      ...state,
      setFilter,
      clearEvents,
      clearTimeMappings,
      reconnect,
      disconnect,
      forceReconnect,
      getTimeMappingForGameTime,
      getLatestTimeMapping,
      getSortedTimeMappings,
      findTimeMappingForTimestamp,
      historyLoaded,
      // Backwards compatibility: provide timeMappingRecords as an alias for timeMappings
      timeMappingRecords: state.timeMappings,
      // Connection state for UI feedback
      eventsConnectionState,
      timeMappingConnectionState,
    }),
    [
      state,
      setFilter,
      clearEvents,
      clearTimeMappings,
      reconnect,
      disconnect,
      forceReconnect,
      getTimeMappingForGameTime,
      getLatestTimeMapping,
      getSortedTimeMappings,
      findTimeMappingForTimestamp,
      historyLoaded,
      eventsConnectionState,
      timeMappingConnectionState,
    ],
  );

  return <SportEventContext.Provider value={contextValue}>{children}</SportEventContext.Provider>;
};

export const useSportEvents = (): SportEventContextValue => {
  const context = useContext(SportEventContext);
  if (!context) {
    throw new Error('useSportEvents must be used within a SportEventProvider');
  }
  return context;
};
